/**
 * Protected hours: per weekday, an owner can switch BTB off or protect a clock-time range in which
 * no BTB block may sit. Pure data and helpers; imports nothing so the web app can use it too.
 * Weekdays are numbered Sunday = 0 … Saturday = 6.
 */

export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;
export const WEEKDAYS: readonly Weekday[] = [0, 1, 2, 3, 4, 5, 6];
export const WEEKDAY_NAMES: Record<Weekday, string> = {
  0: "Sunday",
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
};

export interface DayRule { btb: boolean; protectFrom: string | null; protectTo: string | null }
export type ProtectedHours = Record<Weekday, DayRule>;

export const DEFAULT_DAY_RULE: DayRule = { btb: true, protectFrom: null, protectTo: null };

export function defaultProtectedHours(): ProtectedHours {
  return Object.fromEntries(WEEKDAYS.map((d) => [d, { ...DEFAULT_DAY_RULE }])) as ProtectedHours;
}

/** "HH:mm" → minutes since midnight; "24:00" → 1440. Null for anything else. */
export function parseClock(hhmm: string): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (min > 59) return null;
  if (h === 24) return min === 0 ? 1440 : null;
  return h <= 23 ? h * 60 + min : null;
}

/** Minutes since midnight → "11am", "10:30am", "midnight" (0 and 1440), "12pm" (noon is not used). */
export function clockLabel(minutes: number): string {
  if (minutes === 0 || minutes === 1440) return "midnight";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}${m === 0 ? "" : `:${String(m).padStart(2, "0")}`}${h < 12 ? "am" : "pm"}`;
}

/** The protected range in minutes, or null when the day protects nothing or is off. */
export function protectedRange(rule: DayRule): { from: number; to: number } | null {
  if (!rule.btb || rule.protectFrom === null || rule.protectTo === null) return null;
  const from = parseClock(rule.protectFrom);
  const to = parseClock(rule.protectTo);
  if (from === null || to === null || to <= from) return null;
  return { from, to };
}

/** True when [startMin, endMin) overlaps the day's range by more than zero minutes. */
export function insideProtected(rule: DayRule, startMin: number, endMin: number): boolean {
  const r = protectedRange(rule);
  return r !== null && startMin < r.to && endMin > r.from;
}

export function isDefaultDay(rule: DayRule): boolean {
  return rule.btb && rule.protectFrom === null && rule.protectTo === null;
}

/** Salon hours for the rail (display only). Shared so src/blvd, src/api and src/web agree. */
export interface DayHours { open: string; close: string }               // "HH:mm"
export type WeekHours = Record<Weekday, DayHours | null>;              // null = no hours listed that day
export const DEFAULT_DAY_HOURS: DayHours = { open: "07:00", close: "21:00" };

/** Per weekday, the earliest open and latest close among the weeks that have hours that day; null when none do. */
export function unionWeekHours(weeks: readonly WeekHours[]): WeekHours {
  return Object.fromEntries(WEEKDAYS.map((d) => {
    const days = weeks.map((w) => w[d]).filter((h): h is DayHours => h !== null);
    if (days.length === 0) return [d, null];
    return [d, { open: days.map((h) => h.open).reduce((a, b) => (b < a ? b : a)), close: days.map((h) => h.close).reduce((a, b) => (b > a ? b : a)) }];
  })) as WeekHours;
}

/** A time read from storage: absent/null → null, a valid clock → itself, anything else → undefined (malformed). */
function readTime(v: unknown): string | null | undefined {
  if (v === undefined || v === null) return null;
  return typeof v === "string" && parseClock(v) !== null ? v : undefined;
}

/** Fills missing / malformed days with the default; never throws. For reading stored jsonb and lenient PUT bodies. */
export function normalizeProtectedHours(raw: unknown): ProtectedHours {
  const out = defaultProtectedHours();
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return out;
  const src = raw as Record<string, unknown>;
  for (const d of WEEKDAYS) {
    const day = src[String(d)];
    if (typeof day !== "object" || day === null) continue;
    const { btb, protectFrom, protectTo } = day as Record<string, unknown>;
    const from = readTime(protectFrom);
    const to = readTime(protectTo);
    if (typeof btb !== "boolean" || from === undefined || to === undefined) continue;
    // A day with BTB off protects nothing.
    out[d] = btb ? { btb, protectFrom: from, protectTo: to } : { btb, protectFrom: null, protectTo: null };
  }
  return out;
}
